import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';
import { PostAuthRoutes } from 'c-routes';
import { callApi } from 'c-data-layer';
import ApiClient from 'c-data/apiClient';
import type { DashboardCampaignLink } from './dashboardCampaignLink';
import { campaignLookupRequest } from './dashboardCampaignLink';

type QueryFilter = { field: string; operator: string; value: string | number | string[] };

// Opens the campaign a table row is about: straight away when the row has its id, after asking
// the report for it when only its reference or name is shown. `notFound` is set when the lookup
// comes back empty (the campaign is gone, or out of reach).
const useOpenDashboardCampaign = (filters: QueryFilter[]) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const [looking, setLooking] = useState(false);
    const [notFound, setNotFound] = useState(false);

    const go = useCallback(
        (id: string) =>
            history.push(generatePath(PostAuthRoutes.Campaigns.CampaignOverview, { id })),
        [history],
    );

    const open = useCallback(
        (link: DashboardCampaignLink) => {
            setNotFound(false);
            if (link.kind === 'id') {
                go(link.id);
                return;
            }
            const { request } = ApiClient.Reports.dashboard(
                campaignLookupRequest(link, filters) as any,
            );
            setLooking(true);
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore - thunk dispatch typing, mirrors useDashboardReport
            dispatch(callApi(request))
                .then(([err, , response]: [any, any, any]) => {
                    setLooking(false);
                    const id = err ? null : response?.data?.[0]?.campaign_id;
                    if (id == null || String(id) === '') setNotFound(true);
                    else go(String(id));
                    return undefined;
                })
                .catch(() => {
                    setLooking(false);
                    setNotFound(true);
                });
        },
        [dispatch, filters, go],
    );

    return { open, looking, notFound, clearNotFound: () => setNotFound(false) };
};

export default useOpenDashboardCampaign;
